import { useFormContext, Controller } from 'react-hook-form'
import { defaultTheme } from '../../../theme/index'
import styled from 'styled-components'
import React, { CSSProperties, ReactElement } from 'react'
import { Box } from '@mui/material'
import { RHFFieldRules } from '../../../features/lists/components/AddToList/Stages/Prompt/Methods/Manual/Table/Utils'

export interface CustomFieldProps {
  /**
   * @deprecated Use {@link CustomFieldProps.wrapperStyle} and {@link CustomFieldProps.inputStyle} instead.
   */
  width?: string
  label?: string
  maxSize?: number
  validate?: (value: string) => string | undefined
  onChange?: (e: unknown) => void
  type?: 'text' | 'textarea' | 'number'
  note?: string
  autoComplete?: 'on' | 'off'
  wrapperStyle?: CSSProperties
  inputStyle?: CSSProperties
  name: string
  rules?: RHFFieldRules
  // Add other input props
  min?: number
  max?: number
  placeholder?: string
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

const CustomField = ({
  width = 'auto',
  label,
  maxSize,
  type = 'text',
  note,
  onChange = () => undefined,
  onKeyDown,
  autoComplete = 'on',
  wrapperStyle,
  inputStyle,
  name,
  validate,
  rules,
  ...props
}: CustomFieldProps): ReactElement => {
  const { control, watch } = useFormContext()
  const fieldValue = watch(name)

  // Combine custom validate function with rules
  const validationRules = {
    ...rules,
    ...(validate && {
      validate: {
        custom: (value: string) => {
          // Convert empty values to empty string for validation
          const stringValue =
            value === undefined || value === null ? '' : String(value)
          return validate(stringValue)
        },
      },
    }),
  }

  return (
    <InputWrapper style={wrapperStyle}>
      <FieldLabel>{label}</FieldLabel>

      <Controller
        name={name}
        control={control}
        rules={validationRules}
        render={({ field, fieldState: { error } }) => {
          // Ensure value is string for input
          const fieldProps = {
            ...field,
            value:
              field.value === undefined || field.value === null
                ? ''
                : String(field.value),
          }

          return (
            <>
              {(type === 'text' || type === 'number') && (
                <Input
                  {...fieldProps}
                  {...props}
                  type={type}
                  onKeyDown={onKeyDown ? (e) => onKeyDown(e) : undefined}
                  onChange={(e) => {
                    const value = e.target.value
                    field.onChange(
                      type === 'number' && value ? Number(value) : value
                    )
                    onChange(value)
                  }}
                  width={width}
                  error={!!error}
                  maxLength={maxSize}
                  autoComplete={autoComplete}
                  style={inputStyle}
                  min={props.min}
                  max={props.max}
                />
              )}
              {type === 'textarea' && (
                <TextArea
                  {...fieldProps}
                  {...props}
                  width={width}
                  onChange={(e) => {
                    const value = e.target.value
                    field.onChange(value)
                    onChange(value)
                  }}
                  maxLength={maxSize}
                  error={!!error}
                  style={inputStyle}
                />
              )}
              {maxSize && (
                <SizeText error={String(fieldValue || '').length > maxSize}>
                  {String(fieldValue || '').length}/{maxSize}
                </SizeText>
              )}
              {error?.message && (
                <ErrorText className="error">{error.message}</ErrorText>
              )}
            </>
          )
        }}
      />

      {note && (
        <Box display="flex" justifyContent="flex-start">
          <Note>{note}</Note>
        </Box>
      )}
    </InputWrapper>
  )
}

export default CustomField

// ... rest of the styled components remain the same
const ErrorText = styled.h6`
  font-family: Roboto;
  margin-top: 5px;
  margin-bottom: 0px;
  font-size: 10px;
  color: ${defaultTheme.colors.red};
`

interface SizeTextProps {
  error: boolean
}
const SizeText = styled.h6<SizeTextProps>`
  font-family: Roboto;
  margin-top: 5px;
  margin-bottom: 0px;
  font-size: 10px;
  color: ${(p) => (p.error ? defaultTheme.colors.red : 'gray')};
  align-self: flex-start;
`

const FieldLabel = styled.span`
  font-family: Roboto, serif;
  font-size: 14px;
  line-height: 24px;
  align-self: flex-start;
  font-weight: bold;
`

const InputWrapper = styled.div`
  margin: 10px;
  display: flex;
  flex-direction: column;
`

interface InputProps {
  error?: boolean
  width: string
}

const Input = styled.input<InputProps>`
  min-height: 48px;
  width: ${(p) => p.width};
  padding: 8px 16px 8px 16px;
  background: ${defaultTheme.colors.componentsBackground};
  border: 1px solid
    ${(p) =>
      p.error ? defaultTheme.colors.red : defaultTheme.colors.greyMedium};
  border-radius: 4px;
`

const TextArea = styled.textarea<InputProps>`
  min-height: 48px;
  padding: 8px 16px 8px 16px;
  background: ${defaultTheme.colors.componentsBackground};
  border: 1px solid
    ${(p) =>
      p.error ? defaultTheme.colors.red : defaultTheme.colors.greyMedium};
  border-radius: 4px;
`

const Note = styled.span`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  line-height: 13px;
  margin-top: 8px;
  color: ${defaultTheme.colors.almostBlack};
`
